import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FileAwarenessMonitorErrorTypeTag, useFileAwarenessModalOptions } from '@wix/media-manager-shared';
import { ModalName } from '../../../../constants/ModalName';
import { useModal, useModalHandlersAndOptions } from '../useModal';
import { ItemType } from '../../../../constants/ItemType';
import { MediaManagerSDKAnyItem } from '../../../media-manager-sdk';
import { bi } from '../../../../services/loggers/bi';
import { useNotificatorConsumer } from '../../useNotificator';

type FileAwarenessModalParams = {
  onDeleted: (args: PermanentDeleteSubscribeArgs) => void;
};

type FileAwarenessOpenOptions = {
  additionalOptions: { selectedItems: MediaManagerSDKAnyItem[]; clickOrigin: string };
  closeModal: () => void;
};

import { captureException } from '@sentry/react';
import { permanentDeleteErrorNotification } from '../../../../containers/FilesManager/notifications/permanentDeleteErrorNotification';
import { PermanentDeleteSubscribeArgs } from '../../../hoc/withItemsActions/withItemsActions.types';
import { usePrivateMediaProxyMediaManagerService } from '../../usePrivateMediaProxy';

export const useFileAwarenessModalHandlers = () => useModalHandlersAndOptions(ModalName.FileAwareness);

export const useFileAwarenessModal = ({ onDeleted }: FileAwarenessModalParams) => {
  const { formatMessage, formatDate } = useIntl();
  const { closeModal, additionalOptions }: FileAwarenessOpenOptions = useFileAwarenessModalHandlers();
  const { notify } = useNotificatorConsumer();
  const mediaManagerService = usePrivateMediaProxyMediaManagerService();

  const [fileIds = [], folderIds = []] = useMemo(
    () =>
      additionalOptions?.selectedItems?.reduce<[string[], string[]]>(
        (acc, currentItem) => {
          const [files, foldersAndBoards] = acc;

          if (currentItem.kind === ItemType.File) {
            files.push(currentItem.id);
          } else {
            foldersAndBoards.push(currentItem.id);
          }
          return acc;
        },
        [[], []],
      ) ?? [],
    [additionalOptions.selectedItems],
  );

  const onDeleteError = () => {
    notify(permanentDeleteErrorNotification());
  };

  const fileAwarenessModalOptions = useFileAwarenessModalOptions({
    folderIds,
    fileIds,
    clickOrigin: additionalOptions?.clickOrigin,
    pageName: 'Media Manager',
    mediaManagerService,
    biLogger: bi.logger(),
    formatMessage,
    formatDate,
    closeModal,
    errorLogger: (error: Error) =>
      captureException(error, {
        tags: {
          [FileAwarenessMonitorErrorTypeTag]: 'file-awareness',
        },
      }),
    onDeleteError,
    onFilesDeleted: onDeleted,
  });

  return useModal(ModalName.FileAwareness, { ...fileAwarenessModalOptions, closeOnSubmit: false });
};
